import axios from 'axios';
import { setNetworkError } from '../actions/siteActions';
import * as actions from '../constants/action-types';
import {
	API_URL,
	GET_CANDIDATES_URL,
	GET_CANDIDATE_REFERENCES_URL,
	GET_REQUEST_SETTINGS_URL,
	POST_ADD_CANDIDATE_TAG_URL,
	POST_ARCHIVE_CANDIDATE_URL,
	POST_CANCEL_ID_CHECK_URL,
	POST_CANCEL_REFEREE_REMINDER_URL,
	POST_CANCEL_REQUEST_REMINDER_URL,
	POST_CANDIDATE_COMMENT_URL,
	POST_CHECK_FOR_EXISTING_REQUEST_EMAIL,
	POST_DELETE_CANDIDATE_URL,
	POST_DELETE_REFERENCE_URL,
	POST_EDIT_NUM_OF_REFERENCES_URL,
	POST_REMOVE_CANDIDATE_TAG_URL,
	POST_REQUEST_SETTINGS_URL,
	GET_REFEREE_SETTINGS_URL,
	POST_REFEREE_SETTINGS_URL,
	POST_REFERENCE_ARCHIVE_URL,
	POST_REFERENCE_UNARCHIVE_URL,
	POST_SEND_CANDIDATE_REMINDER_URL,
	POST_SEND_REFEREE_REMINDER_URL,
	POST_UNARCHIVE_CANDIDATE_URL,
} from '../constants/api.js';
import { updateLoggedInStatus } from './loginActions.js';
import { getTagsList } from './settingsActions';
import { track } from '../utils/analytics';

import { notificationMessage } from './notificationMessageActions';

export const getCandidate = (candidateId) => (dispatch) => {
	const url = API_URL + GET_CANDIDATES_URL;
	const config = {
		params: {
			pg: 1,
			id: candidateId,
		},
		withCredentials: true,
	};

	dispatch({
		type: actions.GET_CANDIDATE,
		payload: {
			candidateId,
		},
	});

	axios
		.get(url, config)
		.then(({ data }) => {
			if (data.status === 'success') {
				dispatch(updateLoggedInStatus(data));
				dispatch(receivedCandidate(data.data[0]));
				dispatch(getCandidateReferences(candidateId));
			}
		})
		.catch((error) => {
			dispatch(setNetworkError(error));
		});
};

const receivedCandidate = (candidate) => ({
	type: actions.RECEIVED_CANDIDATE,
	payload: {
		candidate,
	},
});

let candidatesRequestId = 0;

export const getCandidates =
	(start = 0, order, filters = {}, isFiltered = false, searchQuery = '') =>
	(dispatch, getState) => {
		const requestId = ++candidatesRequestId;
		const url = API_URL + GET_CANDIDATES_URL;
		console.log('filters', filters);
		const config = {
			params: {
				start,
				...(order && { order }),
				active_status: 'active',
				...filters,
			},
			withCredentials: true,
			timeout: 30000,
		};
		const params = new URLSearchParams();

		params.append('q', searchQuery);

		dispatch({
			type: actions.GET_CANDIDATES,
			payload: {
				start,
				isFiltered,
				requestId,
			},
		});

		return axios
			.post(url, params, config)
			.then(({ data }) => {
				if (getState().candidates.candidatesRequestId !== requestId) return;
				dispatch(updateLoggedInStatus(data));
				if (data.status === 'success' && Array.isArray(data.data)) {
					dispatch(receivedCandidates(data.data, start, order));
					dispatch(getTagsList());
				} else {
					throw new Error('Unable to load requests.');
				}
			})
			.catch((error) => {
				if (getState().candidates.candidatesRequestId !== requestId) return;
				dispatch({
					type: actions.GET_CANDIDATES_FAILED,
					payload: { retryArgs: [start, order, filters, isFiltered, searchQuery] },
				});
				dispatch(setNetworkError(error));
			});
	};

export const retryCandidates = () => (dispatch, getState) => {
	const { failedCandidatesRequest, loadingCandidates, loadingMoreCandidates } =
		getState().candidates;
	if (failedCandidatesRequest && !loadingCandidates && !loadingMoreCandidates) {
		return dispatch(getCandidates(...failedCandidatesRequest));
	}
};

const receivedCandidates = (candidates, start, sortOrder) => (dispatch) => {
	const currentActiveId = (candidates[0] && candidates[0].rid) || 0;

	dispatch({
		type: actions.RECEIVED_CANDIDATES,
		payload: {
			activeCandidateId: currentActiveId,
			candidates,
			start,
			sortOrder,
		},
	});

	if (currentActiveId) {
		dispatch(getCandidateReferences(currentActiveId));
	}
};

export const getCandidateReferences = (candidateId) => (dispatch) => {
	const url = API_URL + GET_CANDIDATE_REFERENCES_URL + candidateId;
	const config = { withCredentials: true };

	dispatch({
		type: actions.GET_CANDIDATE_REFERENCES,
		payload: {
			candidateId,
		},
	});

	axios
		.get(url, config)
		.then(({ data }) => {
			dispatch(updateLoggedInStatus(data));
			dispatch(receivedCandidateReferences(candidateId, data.data));
		})
		.catch((error) => {
			dispatch(setNetworkError(error));
		});
};

const receivedCandidateReferences = (candidateId, references) => {
	const updatedReferences = references.map((reference) => {
		const fraudDetected =
			!reference.is_gap &&
			(reference.fraudDetection.ipMatchesCandidate ||
				reference.fraudDetection.ipMatchesReferee ||
				reference.fraudDetection.browseridMatchesCandidate ||
				reference.fraudDetection.onKnownBadActorList ||
				reference.fraudDetection.domainAgeFailed);

		return {
			...reference,
			fraudDetected,
		};
	});

	return {
		type: actions.RECEIVED_CANDIDATE_REFERENCES,
		payload: {
			candidateId,
			references: updatedReferences,
		},
	};
};

export const setActiveReference = (activeReferenceId) => ({
	type: actions.SET_ACTIVE_REFERENCE,
	payload: {
		activeReferenceId,
	},
});

const getNextCandidate = (candidates, id) => {
	if (Object.keys(candidates).length <= 1) return null;

	const keys = Object.keys(candidates);
	const location = keys.indexOf(String(id));

	if (location > -1) {
		const prevValue = candidates[keys[location - 1]];
		const nextValue = candidates[keys[location + 1]];

		return prevValue.rid || nextValue.rid;
	}

	return null;
};

export const deleteCandidate = (id) => (dispatch, getState) => {
	const requestUrl = `${API_URL}${POST_DELETE_CANDIDATE_URL}/${id}`;
	const params = new URLSearchParams();
	const config = { withCredentials: true };

	dispatch({ type: actions.DELETE_CANDIDATE, payload: { candidateId: id } });

	axios
		.post(requestUrl, params, config)
		.then(({ data }) => {
			if (data.status === 'success') {
				dispatch({
					type: actions.CANDIDATE_DELETED,
					payload: {
						candidateId: id,
					},
				});
				dispatch(
					notificationMessage({
						type: 'success',
						textKey: 'Requests.sidebar.deletedCandidateMsg',
					})
				);
				// Refresh the candidates list with current filters
				dispatch(refreshCandidatesWithCurrentFilters());
			} else {
				dispatch(deleteCandidateFailed(id));
			}
		})
		.catch((error) => {
			dispatch(deleteCandidateFailed(id));
			console.error(error);
		});
};

const deleteCandidateFailed = (candidateId) => (dispatch) => {
	dispatch(
		notificationMessage({
			type: 'error',
			textKey: 'Requests.sidebar.deleteCandidateFailedMsg',
		})
	);
	dispatch({
		type: actions.CANDIDATE_DELETE_FAILED,
		payload: {
			candidateId,
		},
	});
};

export const archiveCandidate = (id) => (dispatch, getState) => {
	const requestUrl = `${API_URL}${POST_ARCHIVE_CANDIDATE_URL}/${id}`;
	const params = new URLSearchParams();
	const config = { withCredentials: true };

	dispatch({ type: actions.ARCHIVE_CANDIDATE, payload: { candidateId: id } });

	axios
		.post(requestUrl, params, config)
		.then(({ data }) => {
			if (data.status === 'success') {
				dispatch({
					type: actions.CANDIDATE_ARCHIVED,
					payload: {
						candidateId: id,
					},
				});
				dispatch(
					notificationMessage({
						type: 'success',
						textKey: 'Requests.sidebar.archivedCandidateMsg',
					})
				);
				// Refresh the candidates list with current filters
				dispatch(refreshCandidatesWithCurrentFilters());
			} else {
				dispatch(archiveCandidateFailed(id));
			}
		})
		.catch((error) => {
			dispatch(archiveCandidateFailed(id));
			console.error(error);
		});
};

const archiveCandidateFailed = (candidateId) => (dispatch) => {
	dispatch(
		notificationMessage({
			type: 'error',
			textKey: 'Requests.sidebar.archiveCandidateFailedMsg',
		})
	);
	dispatch({
		type: actions.CANDIDATE_ARCHIVE_FAILED,
		payload: {
			candidateId,
		},
	});
};

export const unarchiveCandidate = (id) => (dispatch, getState) => {
	const requestUrl = `${API_URL}${POST_UNARCHIVE_CANDIDATE_URL}/${id}`;
	const params = new URLSearchParams();
	const config = { withCredentials: true };

	dispatch({ type: actions.UNARCHIVE_CANDIDATE, payload: { candidateId: id } });

	axios
		.post(requestUrl, params, config)
		.then(({ data }) => {
			if (data.status === 'success') {
				dispatch({
					type: actions.CANDIDATE_UNARCHIVED,
					payload: {
						candidateId: id,
					},
				});
				dispatch(
					notificationMessage({
						type: 'success',
						textKey: 'Requests.sidebar.unarchivedCandidateMsg',
					})
				);
				// Refresh the candidates list with current filters
				dispatch(refreshCandidatesWithCurrentFilters());
			} else {
				dispatch(unarchiveCandidateFailed(id));
			}
		})
		.catch((error) => {
			dispatch(unarchiveCandidateFailed(id));
			console.error(error);
		});
};

const unarchiveCandidateFailed = (candidateId) => (dispatch) => {
	dispatch(
		notificationMessage({
			type: 'error',
			textKey: 'Requests.sidebar.unarchiveCandidateFailedMsg',
		})
	);
	dispatch({
		type: actions.CANDIDATE_UNARCHIVE_FAILED,
		payload: {
			candidateId,
		},
	});
};

export const cancelRequestReminders = (rid) =>
	cancelReminders(POST_CANCEL_REQUEST_REMINDER_URL, rid);

export const cancelRefereeReminders = (rfid) =>
	cancelReminders(POST_CANCEL_REFEREE_REMINDER_URL, rfid);

const cancelReminders = (url, id) => (dispatch, getState) => {
	const requestUrl = `${API_URL}${url}/${id}`;
	const params = new URLSearchParams();
	const config = { withCredentials: true };
	const { activeCandidateId } = getState().candidates;

	dispatch(setCancelReminderLoader(activeCandidateId, true));

	axios
		.post(requestUrl, params, config)
		.then(({ data }) => {
			if (data.status === 'success') {
				dispatch(
					notificationMessage({
						type: 'success',
						textKey: 'Requests.timeline.cancelRemindersSuccessMsg',
					})
				);
				dispatch(getCandidate(activeCandidateId));
			} else {
				dispatch(cancelRemindersFailed());
			}
		})
		.catch((error) => {
			dispatch(cancelRemindersFailed());
			console.error(error);
		})
		.finally(() => dispatch(setCancelReminderLoader(activeCandidateId)));
};

const setCancelReminderLoader = (candidateId, isCancelling = false) => ({
	type: actions.SET_CANCEL_REMINDER_LOADER,
	payload: {
		candidateId,
		isCancelling,
	},
});

const cancelRemindersFailed = notificationMessage({
	type: 'error',
	textKey: 'Requests.timeline.cancelRemindersErrorMsg',
});

export const sendCandidateReminder = (rid) => sendReminder(POST_SEND_CANDIDATE_REMINDER_URL, rid);

export const sendRefereeReminder = (rfid) => sendReminder(POST_SEND_REFEREE_REMINDER_URL, rfid);

export const startResumeRefereeReminders = (rfid) =>
	startResumeReminders(POST_SEND_REFEREE_REMINDER_URL, rfid);

const sendReminder = (url, id) => (dispatch, getState) => {
	const requestUrl = `${API_URL}${url}/${id}`;
	const params = new URLSearchParams();
	const config = { withCredentials: true };
	const { activeCandidateId } = getState().candidates;

	dispatch(setSendReminderLoader(activeCandidateId, true));

	axios
		.post(requestUrl, params, config)
		.then(({ data }) => {
			if (data.status === 'success') {
				dispatch(
					notificationMessage({
						type: 'success',
						textKey: 'Requests.timeline.sendReminderSuccessMsg',
					})
				);
				dispatch(getCandidate(activeCandidateId));
			} else {
				dispatch(sendReminderFailed());
			}
		})
		.catch((error) => {
			dispatch(sendReminderFailed());
			console.error(error);
		})
		.finally(() => dispatch(setSendReminderLoader(activeCandidateId)));
};

const setSendReminderLoader = (candidateId, isSendingReminder = false) => ({
	type: actions.SET_SEND_REMINDER_LOADER,
	payload: {
		candidateId,
		isSendingReminder,
	},
});

const sendReminderFailed = notificationMessage({
	type: 'error',
	textKey: 'Requests.timeline.sendReminderErrorMsg',
});

const startResumeReminders = (url, id) => (dispatch, getState) => {
	const requestUrl = `${API_URL}${url}/${id}`;
	const params = new URLSearchParams();
	const config = { withCredentials: true };
	const { activeCandidateId } = getState().candidates;

	dispatch(setSendReminderLoader(activeCandidateId, true));

	axios
		.post(requestUrl, params, config)
		.then(({ data }) => {
			if (data.status === 'success') {
				dispatch(
					notificationMessage({
						type: 'success',
						textKey: 'Requests.timeline.startResumeReminderSuccessMsg',
					})
				);
				dispatch(getCandidate(activeCandidateId));
			} else {
				dispatch(startResumeRemindersFailed());
			}
		})
		.catch((error) => {
			dispatch(startResumeRemindersFailed());
			console.error(error);
		})
		.finally(() => dispatch(setSendReminderLoader(activeCandidateId)));
};

const startResumeRemindersFailed = notificationMessage({
	type: 'error',
	textKey: 'Requests.timeline.startResumeReminderErrorMsg',
});

export const deleteReference = (rfid) => (dispatch, getState) => {
	const requestUrl = `${API_URL}${POST_DELETE_REFERENCE_URL}/${rfid}`;
	const params = new URLSearchParams();
	const config = { withCredentials: true };
	const { activeCandidateId } = getState().candidates;

	dispatch({ type: actions.DELETE_REFERENCE });

	axios
		.post(requestUrl, params, config)
		.then(({ data }) => {
			if (data.status === 'success') {
				dispatch(
					notificationMessage({
						type: 'success',
						textKey: 'Requests.timeline.deleteReferenceSuccessMsg',
					})
				);
				dispatch({ type: actions.DELETE_REFERENCE_SUCCESS });
				dispatch(getCandidateReferences(activeCandidateId));
				dispatch(getCandidate(activeCandidateId));
			} else {
				dispatch(deleteReferenceFailed());
			}
		})
		.catch((error) => {
			dispatch(deleteReferenceFailed());
			console.error(error);
		});
};

const deleteReferenceFailed = () => (dispatch) => {
	dispatch(
		notificationMessage({
			type: 'error',
			textKey: 'Requests.timeline.deleteReferenceErrorMsg',
		})
	);

	dispatch({ type: actions.DELETE_REFERENCE_FAILED });
};

export const editNumberOfReferences = (numOfRefs) => (dispatch, getState) => {
	const { activeCandidateId, byId } = getState().candidates;
	const requestUrl = `${API_URL}${POST_EDIT_NUM_OF_REFERENCES_URL}/${activeCandidateId}`;
	const params = new URLSearchParams();
	const config = { withCredentials: true };
	const previousReferencesRequested = byId[activeCandidateId].refs_requested;

	if (previousReferencesRequested === numOfRefs) {
		return dispatch(
			notificationMessage({
				type: 'info',
				textKey: 'Requests.timeline.editNumOfReferencesInfoMsg',
			})
		);
	}

	params.append('inputrefnum', numOfRefs);

	dispatch({ type: actions.EDIT_NUM_OF_REFERENCES });

	axios
		.post(requestUrl, params, config)
		.then(({ data }) => {
			if (data.status === 'success') {
				dispatch(
					notificationMessage({
						type: 'success',
						textKey: 'Requests.timeline.editNumOfReferencesSuccessMsg',
						textValues: {
							previousNumber: previousReferencesRequested,
							newNumber: numOfRefs,
						},
					})
				);
				dispatch({ type: actions.EDIT_NUM_OF_REFERENCES_SUCCESS });
				dispatch(getCandidate(activeCandidateId));
			} else {
				dispatch(editNumberOfReferencesFailed());
			}
		})
		.catch((error) => {
			console.log(error);
			dispatch(editNumberOfReferencesFailed());
			console.error(error);
		});
};

const editNumberOfReferencesFailed = () => (dispatch) => {
	dispatch(
		notificationMessage({
			type: 'error',
			textKey: 'Requests.timeline.editNumOfReferencesErrorMsg',
		})
	);

	dispatch({ type: actions.EDIT_NUM_OF_REFERENCES_FAILED });
};

export const postCandidateComment = (rid, commentText) => (dispatch) => {
	const requestUrl = `${API_URL}${POST_CANDIDATE_COMMENT_URL}`;
	const params = new URLSearchParams();
	const config = { withCredentials: true };

	params.append('rid', rid);
	params.append('comment_text', commentText);

	dispatch({ type: actions.POST_CANDIDATE_COMMENT });

	axios
		.post(requestUrl, params, config)
		.then(({ data }) => {
			if (data.status === 'success') {
				dispatch({ type: actions.POST_CANDIDATE_COMMENT_SUCCESS });
				dispatch(getCandidate(rid));
				dispatch(
					notificationMessage({
						type: 'success',
						textKey: 'Requests.sidebar.postCommentSuccessMsg',
					})
				);
			} else {
				dispatch(postCandidateCommentFailed());
			}
		})
		.catch((error) => {
			dispatch(postCandidateCommentFailed());
			console.error(error);
		});
};

const postCandidateCommentFailed = () => (dispatch) => {
	dispatch(
		notificationMessage({
			type: 'error',
			textKey: 'Requests.sidebar.postCommentErrorMsg',
		})
	);
	dispatch({ type: actions.POST_CANDIDATE_COMMENT_FAILED });
};

export const addCandidateTag = (rid, tagId) => async (dispatch) => {
	try {
		const response = await axios.post(
			`${API_URL}${POST_ADD_CANDIDATE_TAG_URL}`,
			new URLSearchParams({
				rid,
				tag_id: tagId,
			}),
			{ withCredentials: true }
		);

		if (response.data.status === 'success') {
			dispatch(updateCandidateTags(rid, response.data.data.tags));
		} else {
			throw new Error('Failed to add tag');
		}
	} catch (error) {
		dispatch(
			notificationMessage({
				type: 'error',
				textKey: 'Requests.sidebar.tagAddedError',
			})
		);
	}
};

export const removeCandidateTag = (rid, tagId) => async (dispatch) => {
	try {
		const response = await axios.post(
			`${API_URL}${POST_REMOVE_CANDIDATE_TAG_URL}`,
			new URLSearchParams({
				rid,
				tag_id: tagId,
			}),
			{ withCredentials: true }
		);

		if (response.data.status === 'success') {
			dispatch(updateCandidateTags(rid, response.data.data.tags));
		} else {
			throw new Error('Failed to remove tag');
		}
	} catch (error) {
		dispatch(
			notificationMessage({
				type: 'error',
				textKey: 'Requests.sidebar.tagRemovedError',
			})
		);
	}
};

const updateCandidateTags = (candidateId, tags) => ({
	type: actions.UPDATE_CANDIDATE_TAGS,
	payload: {
		candidateId,
		tags,
	},
});

export const refreshCandidatesWithCurrentFilters = () => (dispatch, getState) => {
	const { candidates } = getState();
	const { currentFilters = {}, currentSearchValue = '', currentSortBy = 'newest' } = candidates;

	dispatch({
		type: actions.REFRESH_CANDIDATES_WITH_FILTERS,
	});

	dispatch(getCandidates(0, currentSortBy, currentFilters, true, currentSearchValue));
};

export const setCandidateFilters = (filters, searchValue = '', sortBy = 'newest') => ({
	type: actions.SET_CANDIDATE_FILTERS,
	payload: {
		filters,
		searchValue,
		sortBy,
	},
});

// Request Settings Actions
export const getRequestSettings = (requestId) => async (dispatch) => {
	dispatch({ type: actions.GET_REQUEST_SETTINGS });

	try {
		const response = await axios.get(`${API_URL}${GET_REQUEST_SETTINGS_URL}${requestId}`, {
			withCredentials: true,
		});

		if (response.data.status === 'success') {
			dispatch({
				type: actions.RECEIVED_REQUEST_SETTINGS,
				payload: response.data.data[0],
			});
		} else {
			dispatch({
				type: actions.ERROR_REQUEST_SETTINGS,
				payload: response.data.message || 'Failed to load request settings',
			});
		}
	} catch (error) {
		console.error('Error fetching request settings:', error);
		dispatch({
			type: actions.ERROR_REQUEST_SETTINGS,
			payload: 'Failed to load request settings',
		});
	}
};

export const updateRequestSettings = (requestId, settings) => async (dispatch, getState) => {
	dispatch({ type: actions.UPDATE_REQUEST_SETTINGS });

	const params = new URLSearchParams();

	// Add all settings to params, converting booleans to 0/1
	Object.entries(settings).forEach(([key, value]) => {
		if (value !== null && value !== undefined) {
			// Convert booleans to 0/1 for backend compatibility
			if (typeof value === 'boolean') {
				params.append(key, value ? 1 : 0);
			} else {
				params.append(key, value);
			}
		}
	});

	try {
		const response = await axios.post(
			`${API_URL}${POST_REQUEST_SETTINGS_URL}${requestId}`,
			params,
			{ withCredentials: true }
		);

		if (response.data.status === 'success') {
			dispatch({ type: actions.UPDATED_REQUEST_SETTINGS });
			dispatch(
				notificationMessage({
					type: 'success',
					textKey: 'Requests.editRequest.updateSuccess',
				})
			);
			// Refresh the candidate data to get updated values
			dispatch(getCandidate(requestId));
			// Refresh the settings
			dispatch(getRequestSettings(requestId));
		} else {
			dispatch({
				type: actions.ERROR_UPDATING_REQUEST_SETTINGS,
				payload: response.data.message || 'Failed to update request settings',
			});
			dispatch(
				notificationMessage({
					type: 'error',
					textKey: 'Requests.editRequest.updateError',
				})
			);
		}
	} catch (error) {
		console.error('Error updating request settings:', error);
		dispatch({
			type: actions.ERROR_UPDATING_REQUEST_SETTINGS,
			payload: 'Failed to update request settings',
		});
		dispatch(
			notificationMessage({
				type: 'error',
				textKey: 'Requests.editRequest.updateError',
			})
		);
	}
};

export const clearRequestSettings = () => ({
	type: actions.CLEAR_REQUEST_SETTINGS,
});

export const setEditRequestModalOpen = (isOpen) => ({
	type: actions.SET_EDIT_REQUEST_MODAL_OPEN,
	payload: isOpen,
});

export const cancelIdCheck = (requestId, digitalIdType) => async (dispatch) => {
	dispatch({ type: actions.CANCEL_ID_CHECK });

	try {
		const params = new URLSearchParams();
		const response = await axios.post(
			`${API_URL}${POST_CANCEL_ID_CHECK_URL}${requestId}`,
			params,
			{ withCredentials: true }
		);

		if (response.data.status === 'success') {
			dispatch({ type: actions.CANCELLED_ID_CHECK });
			dispatch(
				notificationMessage({
					type: 'success',
					textKey: 'Requests.editRequest.basic.cancelIdCheckSuccess',
				})
			);
			track('ID Check Cancelled', { requestId, digitalIdType });
			// Refresh the candidate data to get updated values
			dispatch(getCandidate(requestId));
			// Refresh the settings
			dispatch(getRequestSettings(requestId));
		} else {
			dispatch({
				type: actions.ERROR_CANCEL_ID_CHECK,
				payload: response.data.message || 'Failed to cancel ID check',
			});
			dispatch(
				notificationMessage({
					type: 'error',
					text: response.data.message || 'Failed to cancel ID check',
				})
			);
		}
	} catch (error) {
		console.error('Error cancelling ID check:', error);
		dispatch({
			type: actions.ERROR_CANCEL_ID_CHECK,
			payload: 'Failed to cancel ID check',
		});
		dispatch(
			notificationMessage({
				type: 'error',
				textKey: 'Requests.editRequest.basic.cancelIdCheckError',
			})
		);
	}
};

// Referee Settings Actions
export const getRefereeSettings = (refereeId) => async (dispatch) => {
	dispatch({ type: actions.GET_REFEREE_SETTINGS });

	try {
		const response = await axios.get(`${API_URL}${GET_REFEREE_SETTINGS_URL}${refereeId}`, {
			withCredentials: true,
		});

		if (response.data.status === 'success') {
			dispatch({
				type: actions.RECEIVED_REFEREE_SETTINGS,
				payload: response.data.data[0],
			});
		} else {
			dispatch({
				type: actions.ERROR_REFEREE_SETTINGS,
				payload: response.data.message || 'Failed to load referee settings',
			});
		}
	} catch (error) {
		console.error('Error fetching referee settings:', error);
		dispatch({
			type: actions.ERROR_REFEREE_SETTINGS,
			payload: 'Failed to load referee settings',
		});
	}
};

export const updateRefereeSettings = (refereeId, requestId, settings) => async (dispatch) => {
	dispatch({ type: actions.UPDATE_REFEREE_SETTINGS });

	const params = new URLSearchParams();

	// Add all settings to params
	Object.entries(settings).forEach(([key, value]) => {
		if (value !== null && value !== undefined) {
			params.append(key, value);
		}
	});

	try {
		const response = await axios.post(
			`${API_URL}${POST_REFEREE_SETTINGS_URL}${refereeId}`,
			params,
			{ withCredentials: true }
		);

		if (response.data.status === 'success') {
			dispatch({ type: actions.UPDATED_REFEREE_SETTINGS });
			dispatch(
				notificationMessage({
					type: 'success',
					textKey: 'Requests.editReferee.updateSuccess',
				})
			);
			// Refresh the candidate data to get updated values
			dispatch(getCandidate(requestId));
			// Refresh the settings
			dispatch(getRefereeSettings(refereeId));
		} else {
			dispatch({
				type: actions.ERROR_UPDATING_REFEREE_SETTINGS,
				payload: response.data.message || 'Failed to update referee settings',
			});
			dispatch(
				notificationMessage({
					type: 'error',
					textKey: 'Requests.editReferee.updateError',
				})
			);
		}
	} catch (error) {
		console.error('Error updating referee settings:', error);
		dispatch({
			type: actions.ERROR_UPDATING_REFEREE_SETTINGS,
			payload: 'Failed to update referee settings',
		});
		dispatch(
			notificationMessage({
				type: 'error',
				textKey: 'Requests.editReferee.updateError',
			})
		);
	}
};

export const clearRefereeSettings = () => ({
	type: actions.CLEAR_REFEREE_SETTINGS,
});

export const setEditRefereeModalOpen = (isOpen, refereeId = null) => ({
	type: actions.SET_EDIT_REFEREE_MODAL_OPEN,
	payload: { isOpen, refereeId },
});

// Reference Archive Actions
export const archiveReference = (referenceId, requestId) => async (dispatch) => {
	dispatch({ type: actions.ARCHIVE_REFERENCE });

	try {
		const params = new URLSearchParams();
		const response = await axios.post(
			`${API_URL}${POST_REFERENCE_ARCHIVE_URL}${referenceId}`,
			params,
			{ withCredentials: true }
		);

		if (response.data.status === 'success') {
			dispatch({ type: actions.ARCHIVED_REFERENCE });
			dispatch(
				notificationMessage({
					type: 'success',
					textKey: 'Requests.timeline.archiveReferenceSuccess',
				})
			);
			// Refresh the candidate data to get updated values
			dispatch(getCandidate(requestId));
		} else {
			dispatch({
				type: actions.ERROR_ARCHIVE_REFERENCE,
				payload: response.data.message || 'Failed to archive reference',
			});
			dispatch(
				notificationMessage({
					type: 'error',
					text: response.data.message || 'Failed to archive reference',
				})
			);
		}
	} catch (error) {
		console.error('Error archiving reference:', error);
		dispatch({
			type: actions.ERROR_ARCHIVE_REFERENCE,
			payload: 'Failed to archive reference',
		});
		dispatch(
			notificationMessage({
				type: 'error',
				textKey: 'Requests.timeline.archiveReferenceError',
			})
		);
	}
};

export const unarchiveReference = (referenceId, requestId) => async (dispatch) => {
	dispatch({ type: actions.UNARCHIVE_REFERENCE });

	try {
		const params = new URLSearchParams();
		const response = await axios.post(
			`${API_URL}${POST_REFERENCE_UNARCHIVE_URL}${referenceId}`,
			params,
			{ withCredentials: true }
		);

		if (response.data.status === 'success') {
			dispatch({ type: actions.UNARCHIVED_REFERENCE });
			dispatch(
				notificationMessage({
					type: 'success',
					textKey: 'Requests.timeline.unarchiveReferenceSuccess',
				})
			);
			// Refresh the candidate data to get updated values
			dispatch(getCandidate(requestId));
		} else {
			dispatch({
				type: actions.ERROR_UNARCHIVE_REFERENCE,
				payload: response.data.message || 'Failed to unarchive reference',
			});
			dispatch(
				notificationMessage({
					type: 'error',
					text: response.data.message || 'Failed to unarchive reference',
				})
			);
		}
	} catch (error) {
		console.error('Error unarchiving reference:', error);
		dispatch({
			type: actions.ERROR_UNARCHIVE_REFERENCE,
			payload: 'Failed to unarchive reference',
		});
		dispatch(
			notificationMessage({
				type: 'error',
				textKey: 'Requests.timeline.unarchiveReferenceError',
			})
		);
	}
};
